import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, FileSearch, Filter, Trash2, Pencil, Eye, EyeOff, Download, Upload, Paperclip, Lock, X, RotateCcw, AlertTriangle, History } from "lucide-react";
import BackToDashBar from "@/components/BackToDashBar";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { OwnerCell } from "@/components/RecordTrail";
import { AdminTxActions } from "@/components/AdminTxActions";
import { STATUS_LABELS, STATUS_STYLE, TX_TYPES, fmtUSD, typeLabel } from "@/lib/tx";
import { PayrollRunPanel } from "@/pages/AccountingPages";

function fmtDate(s) {
  if (!s) return "—";
  return s.length > 10 ? new Date(s).toLocaleDateString() : s;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 5 (2026-08-09) — "+ Create JE" panel. One modal, six types.
//  Every type produces a DRAFT transaction in the Phase 53 pipeline via
//  `POST /api/transactions/je-draft`; admins verify + post to materialise
//  the JE. Attachments picked here are uploaded via `/api/attachments`
//  and linked on the tx doc; the pipeline post-handler transfers them
//  onto the JE at post time.
// ─────────────────────────────────────────────────────────────────────────────
const JE_TYPES = [
  { value: "payroll",        label: "Payroll" },
  { value: "income",         label: "Income" },
  { value: "expense",        label: "Expense" },
  { value: "bank_transfer",  label: "Bank Transfer" },
  { value: "loan_repayment", label: "Loan Repayment" },
  { value: "manual",         label: "Manual / Other" },
];

// Attachment picker used inside the panel. Uploads to /api/attachments
// on drop / file-picker change and returns att_ids to the parent.
function AttachmentPicker({ attIds, setAttIds }) {
  const [items, setItems] = useState([]);   // [{att_id, name, size}]
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const id = data?.id || data?.att_id;
        if (id) {
          setItems((prev) => [...prev, { att_id: id, name: f.name, size: f.size }]);
          setAttIds((prev) => [...(prev || []), id]);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = (i) => {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
    setAttIds((prev) => (prev || []).filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-2" data-testid="cje-attachments">
      <Label className="text-xs">Attachments (optional)</Label>
      <label
        htmlFor="cje-file-input"
        className="flex items-center justify-center gap-2 border border-dashed rounded-md p-3 text-xs text-slate-500 cursor-pointer hover:bg-slate-50 transition-colors"
        onDrop={(e) => { e.preventDefault(); upload(Array.from(e.dataTransfer.files || [])); }}
        onDragOver={(e) => e.preventDefault()}
        data-testid="cje-file-drop"
      >
        <Paperclip className="h-4 w-4" />
        <span>Drag &amp; drop, or click to browse (PDF, JPG, PNG, XLSX, DOCX)</span>
        <input
          id="cje-file-input"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx,.doc"
          className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))}
          data-testid="cje-file-input"
        />
      </label>
      {busy && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading…</div>}
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={it.att_id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1" data-testid={`cje-att-item-${i}`}>
              <span className="truncate">{it.name} <span className="text-slate-400">({Math.round(it.size / 1024)} KB)</span></span>
              <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => remove(i)} data-testid={`cje-att-remove-${i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CreateJEPanel({ onCancel, onCreated, correctionDraft = null }) {
  // Bug bundle #11 (2026-08-18) — when opened from a Reverse & Correct
  // flow, `correctionDraft` carries the prefilled fields for a manual
  // JE that corrects the reversed original. In that mode the type is
  // locked to Manual/Other, an amber banner replaces the usual header
  // description, and the eventual `/transactions/je-draft` payload
  // includes `corrects_je_id` so the JE-post handler auto-links the
  // correcting JE back to the original.
  const isCorrection = !!correctionDraft;
  const [jeType, setJeType] = useState(isCorrection ? "manual" : "payroll");
  const [attIds, setAttIds] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  // Shared draft fields across free-form types.
  const [txDate, setTxDate] = useState(today);
  const [amount, setAmount] = useState("");
  const [narration, setNarration] = useState("");
  const [busy, setBusy] = useState(false);
  // Account catalog for pickers.
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loans, setLoans] = useState([]);
  // Type-specific fields
  const [incomeCode, setIncomeCode] = useState("");
  const [expenseCode, setExpenseCode] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [fromBankId, setFromBankId] = useState("");
  const [toBankId, setToBankId] = useState("");
  const [loanId, setLoanId] = useState("");
  const [principal, setPrincipal] = useState("");
  const [interest, setInterest] = useState("");
  const [manualLines, setManualLines] = useState([
    { account_id: "", debit: "", credit: "" },
    { account_id: "", debit: "", credit: "" },
  ]);
  // Phase 6 (2026-08-09) — Counterparty picker state. Populated for
  // Expense (supplier) and Income (customer). Reference # is the
  // supplier-invoice / customer-invoice number the user is booking
  // against — surfaced as `reference_number` on the tx doc and JE
  // header so AP/AR + CSV export naturally pick it up.
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  // Payroll shape
  const now = new Date();
  const [pyYear, setPyYear] = useState(now.getFullYear());
  const [pyMonth, setPyMonth] = useState(now.getMonth() + 1);
  const [pyPayDate, setPyPayDate] = useState(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10));
  const [pyBankCode, setPyBankCode] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [proposedJe, setProposedJe] = useState(null);
  const [pyOverrides, setPyOverrides] = useState({});
  const [pyBusy, setPyBusy] = useState(false);

  const isBankOrCash = (a) => {
    if (!a || a.type !== "asset" || a.is_active === false) return false;
    const code = String(a.code || "").trim();
    if (!code) return false;
    if (code === "1000" || code === "1100") return true;
    if (a.parent_code === "1010") return true;
    const n = Number(code);
    return Number.isInteger(n) && n >= 1010 && n <= 1099;
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: accs }, br, lr] = await Promise.all([
          api.get("/accounts"),
          api.get("/bank-accounts").catch(() => ({ data: [] })),
          api.get("/loans").catch(() => ({ data: [] })),
        ]);
        const arr = Array.isArray(accs) ? accs : (accs?.items || []);
        setAccounts(arr.filter((a) => a.is_active !== false).sort((a, b) => (a.code || "").localeCompare(b.code || "")));
        const banksList = arr.filter(isBankOrCash);
        setBanks(banksList);
        const bankAccResp = Array.isArray(br.data) ? br.data : (br.data?.items || []);
        setLoans(Array.isArray(lr.data) ? lr.data : (lr.data?.items || []));
        // Use bank_accounts collection for the transfer picker (has id-based selection).
        if (bankAccResp.length) window.__phase5_bank_accounts = bankAccResp;
      } catch { /* noop */ }
    })();
  }, []);

  // Bug bundle #11 (2026-08-18) — Prefill from a Reverse & Correct
  // correction_draft as soon as the accounts list is available (so we
  // can map account_id / code onto the lines picker).
  useEffect(() => {
    if (!correctionDraft || accounts.length === 0) return;
    if (correctionDraft.entry_date) setTxDate(correctionDraft.entry_date);
    if (correctionDraft.narration) setNarration(correctionDraft.narration);
    if (correctionDraft.reference) setReferenceNumber(correctionDraft.reference);
    const rawLines = correctionDraft.prefill_lines || [];
    if (rawLines.length >= 1) {
      // Map each line to the manual editor shape. Accept either
      // account_id (preferred) or account_code as the lookup key.
      const resolved = rawLines.map((ln) => {
        let acc = null;
        if (ln.account_id) acc = accounts.find((a) => a.id === ln.account_id);
        if (!acc && ln.account_code) acc = accounts.find((a) => a.code === ln.account_code);
        return {
          account_id: acc?.id || "",
          debit: Number(ln.debit) || "",
          credit: Number(ln.credit) || "",
        };
      });
      setManualLines(resolved.length >= 2 ? resolved : [
        ...resolved,
        { account_id: "", debit: "", credit: "" },
      ]);
    }
  }, [correctionDraft, accounts]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 6 (2026-08-09) — Load Suppliers and Customers up-front (small
  // masters). Fetched only once regardless of the current jeType so
  // switching between Expense↔Income doesn't burn a round-trip. The
  // active filter is applied client-side against the `is_active` flag.
  useEffect(() => {
    (async () => {
      try {
        const [sup, cus] = await Promise.all([
          api.get("/suppliers").catch(() => ({ data: [] })),
          api.get("/customers").catch(() => ({ data: [] })),
        ]);
        const supArr = Array.isArray(sup.data) ? sup.data : (sup.data?.items || []);
        const cusArr = Array.isArray(cus.data) ? cus.data : (cus.data?.items || []);
        setSuppliers(supArr.filter((s) => s.is_active !== false)
                            .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setCustomers(cusArr.filter((c) => c.is_active !== false)
                            .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch { /* noop */ }
    })();
  }, []);

  const incomeAccounts  = accounts.filter((a) => a.type === "income");
  const expenseAccounts = accounts.filter((a) => a.type === "expense");
  // Bank account rows for the transfer picker come from /bank-accounts.
  const [bankAccountRows, setBankAccountRows] = useState([]);
  useEffect(() => {
    api.get("/bank-accounts").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setBankAccountRows(arr);
    }).catch(() => setBankAccountRows([]));
  }, []);

  // Payroll snapshot loader
  const fetchSnapshot = async () => {
    if (jeType !== "payroll" || !pyYear || !pyMonth) return;
    setPyBusy(true);
    try {
      const overrides = Object.entries(pyOverrides).map(([eid, o]) => ({
        employee_id: eid,
        ...(o.base_salary !== undefined && o.base_salary !== "" ? { base_salary: Number(o.base_salary) } : {}),
        ...(o.paye_tax !== undefined && o.paye_tax !== "" ? { paye_tax: Number(o.paye_tax) } : {}),
        ...(o.other_deductions !== undefined && o.other_deductions !== "" ? { other_deductions: Number(o.other_deductions) } : {}),
        ...(o.variance_note ? { variance_note: o.variance_note } : {}),
      }));
      const { data } = await api.post("/payroll/snapshot", {
        period_year: Number(pyYear),
        period_month: Number(pyMonth),
        payment_date: pyPayDate || undefined,
        bank_account_code: pyBankCode || undefined,
        overrides: overrides.length ? overrides : undefined,
      });
      setSnapshot(data.snapshot);
      setProposedJe(data.proposed_je);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPyBusy(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (jeType === "payroll") fetchSnapshot(); }, [jeType, pyYear, pyMonth, pyPayDate, pyBankCode]);

  const setLine = (i, patch) => setManualLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setManualLines((prev) => [...prev, { account_id: "", debit: "", credit: "" }]);
  const removeLine = (i) => setManualLines((prev) => prev.filter((_, idx) => idx !== i));
  const manualTotDr = manualLines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const manualTotCr = manualLines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const manualBalanced = Math.abs(manualTotDr - manualTotCr) < 0.005 && manualTotDr > 0;

  const varianceRowsMissingNote = (snapshot?.lines || []).filter(
    (l) => l.has_variance && !(pyOverrides[l.employee_id]?.variance_note || l.variance_note),
  );

  // Per-type submit-readiness.
  const canSubmit = (() => {
    if (busy) return false;
    if (jeType === "payroll") {
      return snapshot && Number(snapshot?.totals?.gross || 0) > 0 && pyBankCode && pyPayDate && proposedJe?.balanced && varianceRowsMissingNote.length === 0;
    }
    if (jeType === "income") return txDate && Number(amount) > 0 && incomeCode && bankCode && narration.trim().length >= 3;
    if (jeType === "expense") return txDate && Number(amount) > 0 && expenseCode && bankCode && narration.trim().length >= 3;
    if (jeType === "bank_transfer") return txDate && Number(amount) > 0 && fromBankId && toBankId && fromBankId !== toBankId;
    if (jeType === "loan_repayment") return txDate && loanId && bankCode && ((Number(principal) || 0) + (Number(interest) || 0)) > 0;
    if (jeType === "manual") return txDate && narration.trim().length >= 5 && manualBalanced && manualLines.every((l) => l.account_id);
    return false;
  })();

  // Bug bundle #13 (2026-08-19) — collect per-type "what's still missing"
  // messages so the modal shows a live checklist above Submit and can
  // surface the same list in a tooltip when Submit is disabled.
  const missingReasons = (() => {
    const out = [];
    if (busy) return out;
    if (!txDate && jeType !== "payroll") out.push("Date is required");
    if (jeType === "income" || jeType === "expense") {
      if (!(Number(amount) > 0)) out.push("Amount must be greater than 0");
      if (jeType === "income" && !incomeCode) out.push("Income account is required");
      if (jeType === "expense" && !expenseCode) out.push("Expense account is required");
      if (!bankCode) out.push("Bank / cash account is required");
      const nl = narration.trim().length;
      if (nl < 3) out.push(`Narration (${nl}/3 min characters)`);
    } else if (jeType === "bank_transfer") {
      if (!(Number(amount) > 0)) out.push("Amount must be greater than 0");
      if (!fromBankId) out.push("From-bank is required");
      if (!toBankId) out.push("To-bank is required");
      if (fromBankId && toBankId && fromBankId === toBankId) out.push("From-bank and To-bank must differ");
    } else if (jeType === "loan_repayment") {
      if (!loanId) out.push("Loan is required");
      if (!bankCode) out.push("Bank / cash account is required");
      if (((Number(principal) || 0) + (Number(interest) || 0)) <= 0) {
        out.push("Enter principal and/or interest > 0");
      }
    } else if (jeType === "manual") {
      const nl = narration.trim().length;
      if (nl < 5) out.push(`Narration (${nl}/5 min characters)`);
      const missingAcct = manualLines.reduce((acc, l, i) => (!l.account_id ? acc.concat(i + 1) : acc), []);
      if (missingAcct.length > 0) {
        out.push(`Account required on line ${missingAcct.length === 1 ? missingAcct[0] : missingAcct.join(", ")}`);
      }
      if (manualTotDr + manualTotCr === 0) {
        out.push("Enter Debit and Credit amounts");
      } else if (!manualBalanced) {
        out.push(`Debit must equal Credit (Dr ${fmtUSD(manualTotDr)} vs Cr ${fmtUSD(manualTotCr)})`);
      }
    } else if (jeType === "payroll") {
      if (!pyBankCode) out.push("Pay-from bank is required");
      if (!pyPayDate) out.push("Payment date is required");
      if (!(Number(snapshot?.totals?.gross || 0) > 0)) out.push("Load a payroll snapshot");
      if (varianceRowsMissingNote.length > 0) out.push(`${varianceRowsMissingNote.length} variance row(s) need a note`);
    }
    return out;
  })();

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      let payload = {};
      if (jeType === "payroll") {
        const overrides = Object.entries(pyOverrides).map(([eid, o]) => ({
          employee_id: eid,
          ...(o.base_salary !== undefined && o.base_salary !== "" ? { base_salary: Number(o.base_salary) } : {}),
          ...(o.paye_tax !== undefined && o.paye_tax !== "" ? { paye_tax: Number(o.paye_tax) } : {}),
          ...(o.other_deductions !== undefined && o.other_deductions !== "" ? { other_deductions: Number(o.other_deductions) } : {}),
          ...(o.variance_note ? { variance_note: o.variance_note } : {}),
        }));
        payload = {
          period_year: Number(pyYear), period_month: Number(pyMonth),
          payment_date: pyPayDate, bank_account_code: pyBankCode,
          overrides: overrides.length ? overrides : undefined,
          note: narration || undefined,
        };
      } else if (jeType === "income") {
        // Phase 6 — attach the customer picker + reference # so the
        // resulting tx / JE naturally feed the AR page and CSV export.
        const cust = customers.find((c) => c.id === customerId);
        payload = {
          transaction_date: txDate,
          amount: Number(amount),
          income_account_code: incomeCode,
          bank_account_code: bankCode,
          narration,
          counterparty_id: customerId || undefined,
          counterparty_name: cust?.name || undefined,
          reference_number: referenceNumber || undefined,
        };
      } else if (jeType === "expense") {
        // Phase 6 — attach the supplier picker + reference # so the
        // resulting tx / JE naturally feed the AP page and CSV export.
        const sup = suppliers.find((s) => s.id === supplierId);
        payload = {
          transaction_date: txDate,
          amount: Number(amount),
          expense_account_code: expenseCode,
          bank_account_code: bankCode,
          narration,
          counterparty_id: supplierId || undefined,
          counterparty_name: sup?.name || undefined,
          reference_number: referenceNumber || undefined,
        };
      } else if (jeType === "bank_transfer") {
        payload = { transaction_date: txDate, amount: Number(amount), from_bank_account_id: fromBankId, to_bank_account_id: toBankId, narration };
      } else if (jeType === "loan_repayment") {
        payload = { transaction_date: txDate, loan_id: loanId, principal: Number(principal) || 0, interest: Number(interest) || 0, bank_account_code: bankCode, narration };
      } else if (jeType === "manual") {
        payload = {
          transaction_date: txDate,
          narration,
          lines: manualLines.map((l) => ({ account_id: l.account_id, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })),
        };
      }
      const bodyOut = { type: jeType, payload };
      // Bug bundle #11 (2026-08-18) — attach `corrects_je_id` when in
      // correction mode so the JE post handler can auto-link the pair.
      if (correctionDraft?.corrects_je_id) {
        bodyOut.payload = { ...payload, corrects_je_id: correctionDraft.corrects_je_id };
      }
      const { data: tx } = await api.post("/transactions/je-draft", bodyOut);
      // Link any uploaded attachments to the draft tx.
      if (attIds.length && tx?.id) {
        try {
          await api.patch(`/transactions/${tx.id}`, { attachment_ids: attIds });
        } catch (e) {
          console.warn("Attachment link failed:", e);
        }
      }
      onCreated?.(tx);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 text-sm" data-testid="create-je-panel">
      {/* Bug bundle #11 (2026-08-18) — Correction mode amber banner */}
      {isCorrection && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="correction-banner">
          <strong>Correcting JE</strong> — you&apos;re creating a correcting entry for{" "}
          <span className="font-mono">
            {correctionDraft.original_reference || correctionDraft.corrects_je_id?.slice(0, 8) + "…"}
          </span>
          . The reversal has already been posted. Adjust the amounts/accounts below
          and submit — the correcting JE will link back to the original once posted.
        </div>
      )}
      <div>
        <Label className="text-xs">Type <span className="text-red-500">*</span></Label>
        <Select value={jeType} onValueChange={setJeType} disabled={isCorrection}>
          <SelectTrigger data-testid="cje-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {JE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`cje-type-option-${t.value}`}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isCorrection && (
          <p className="text-[10px] text-slate-500 mt-1">Locked to Manual/Other for correction entries.</p>
        )}
      </div>

      {/* Income */}
      {jeType === "income" && (
        <div className="grid grid-cols-2 gap-3" data-testid="cje-income-fields">
          <div>
            <Label className="text-xs">Date <span className="text-red-500">*</span></Label>
            <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="cje-inc-date" />
          </div>
          <div>
            <Label className="text-xs">Amount <span className="text-red-500">*</span></Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cje-inc-amount" />
          </div>
          <div>
            <Label className="text-xs">Income account <span className="text-red-500">*</span></Label>
            <Select value={incomeCode} onValueChange={setIncomeCode}>
              <SelectTrigger data-testid="cje-inc-account"><SelectValue placeholder="Select income account" /></SelectTrigger>
              <SelectContent>{incomeAccounts.map((a) => (<SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bank / cash account <span className="text-red-500">*</span></Label>
            <Select value={bankCode} onValueChange={setBankCode}>
              <SelectTrigger data-testid="cje-inc-bank"><SelectValue placeholder="Select bank/cash" /></SelectTrigger>
              <SelectContent>{banks.map((a) => (<SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          {/* Phase 6 (2026-08-09) — Customer picker + Reference # feeds AR. */}
          <div>
            <Label className="text-xs">Customer (optional)</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="cje-inc-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.length === 0 && (
                  <SelectItem value="__none" disabled data-testid="cje-inc-customer-empty">
                    No active customers
                  </SelectItem>
                )}
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`cje-inc-customer-opt-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reference # (optional)</Label>
            <Input
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="Invoice #, receipt #, etc."
              data-testid="cje-inc-reference"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs flex items-center justify-between">
              <span>Narration <span className="text-red-500">*</span></span>
              <span
                className={`text-[10px] tabular-nums ${narration.trim().length >= 3 ? "text-emerald-600" : "text-slate-400"}`}
                data-testid="cje-inc-narration-counter"
              >
                {narration.trim().length} / 3 min
              </span>
            </Label>
            <Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} data-testid="cje-inc-narration" />
          </div>
        </div>
      )}

      {/* Expense */}
      {jeType === "expense" && (
        <div className="grid grid-cols-2 gap-3" data-testid="cje-expense-fields">
          <div>
            <Label className="text-xs">Date <span className="text-red-500">*</span></Label>
            <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="cje-exp-date" />
          </div>
          <div>
            <Label className="text-xs">Amount <span className="text-red-500">*</span></Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cje-exp-amount" />
          </div>
          <div>
            <Label className="text-xs">Expense account <span className="text-red-500">*</span></Label>
            <Select value={expenseCode} onValueChange={setExpenseCode}>
              <SelectTrigger data-testid="cje-exp-account"><SelectValue placeholder="Select expense account" /></SelectTrigger>
              <SelectContent>{expenseAccounts.map((a) => (<SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Bank / cash account <span className="text-red-500">*</span></Label>
            <Select value={bankCode} onValueChange={setBankCode}>
              <SelectTrigger data-testid="cje-exp-bank"><SelectValue placeholder="Select bank/cash" /></SelectTrigger>
              <SelectContent>{banks.map((a) => (<SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          {/* Phase 6 (2026-08-09) — Supplier picker + Reference # feeds AP. */}
          <div>
            <Label className="text-xs">Supplier (optional)</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="cje-exp-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>
                {suppliers.length === 0 && (
                  <SelectItem value="__none" disabled data-testid="cje-exp-supplier-empty">
                    No active suppliers
                  </SelectItem>
                )}
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`cje-exp-supplier-opt-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reference # (optional)</Label>
            <Input
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="Supplier invoice #, bill #, etc."
              data-testid="cje-exp-reference"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Narration <span className="text-red-500">*</span></Label>
            <Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} data-testid="cje-exp-narration" />
          </div>
        </div>
      )}

      {/* Bank Transfer */}
      {jeType === "bank_transfer" && (
        <div className="grid grid-cols-2 gap-3" data-testid="cje-bt-fields">
          <div>
            <Label className="text-xs">Date <span className="text-red-500">*</span></Label>
            <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="cje-bt-date" />
          </div>
          <div>
            <Label className="text-xs">Amount <span className="text-red-500">*</span></Label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cje-bt-amount" />
          </div>
          <div>
            <Label className="text-xs">From bank <span className="text-red-500">*</span></Label>
            <Select value={fromBankId} onValueChange={setFromBankId}>
              <SelectTrigger data-testid="cje-bt-from"><SelectValue placeholder="Source bank" /></SelectTrigger>
              <SelectContent>{bankAccountRows.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name} · {b.currency || "USD"}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">To bank <span className="text-red-500">*</span></Label>
            <Select value={toBankId} onValueChange={setToBankId}>
              <SelectTrigger data-testid="cje-bt-to"><SelectValue placeholder="Destination bank" /></SelectTrigger>
              <SelectContent>{bankAccountRows.filter((b) => b.id !== fromBankId).map((b) => (<SelectItem key={b.id} value={b.id}>{b.name} · {b.currency || "USD"}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Narration</Label>
            <Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} data-testid="cje-bt-narration" />
          </div>
        </div>
      )}

      {/* Loan Repayment */}
      {jeType === "loan_repayment" && (
        <div className="grid grid-cols-2 gap-3" data-testid="cje-lr-fields">
          <div>
            <Label className="text-xs">Payment date <span className="text-red-500">*</span></Label>
            <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="cje-lr-date" />
          </div>
          <div>
            <Label className="text-xs">Loan <span className="text-red-500">*</span></Label>
            <Select value={loanId} onValueChange={setLoanId}>
              <SelectTrigger data-testid="cje-lr-loan"><SelectValue placeholder="Select loan" /></SelectTrigger>
              <SelectContent>{loans.map((l) => (<SelectItem key={l.id} value={l.id}>{l.lender_name || l.id}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Principal portion</Label>
            <Input type="number" step="0.01" min="0" value={principal} onChange={(e) => setPrincipal(e.target.value)} data-testid="cje-lr-principal" />
          </div>
          <div>
            <Label className="text-xs">Interest portion</Label>
            <Input type="number" step="0.01" min="0" value={interest} onChange={(e) => setInterest(e.target.value)} data-testid="cje-lr-interest" />
          </div>
          <div>
            <Label className="text-xs">Bank / cash account <span className="text-red-500">*</span></Label>
            <Select value={bankCode} onValueChange={setBankCode}>
              <SelectTrigger data-testid="cje-lr-bank"><SelectValue placeholder="Select bank/cash" /></SelectTrigger>
              <SelectContent>{banks.map((a) => (<SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Narration</Label>
            <Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} data-testid="cje-lr-narration" />
          </div>
        </div>
      )}

      {/* Manual free-form */}
      {jeType === "manual" && (
        <div className="space-y-3" data-testid="cje-manual-fields">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)} data-testid="cje-man-date" />
            </div>
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>Narration <span className="text-red-500">*</span></span>
                {/* Bug bundle #13 (2026-08-19) — live character counter. */}
                <span
                  className={`text-[10px] tabular-nums ${narration.trim().length >= 5 ? "text-emerald-600" : "text-slate-400"}`}
                  data-testid="cje-man-narration-counter"
                >
                  {narration.trim().length} / 5 min
                </span>
              </Label>
              <Input value={narration} onChange={(e) => setNarration(e.target.value)}
                     placeholder="Min 5 characters"
                     data-testid="cje-man-narration" />
            </div>
          </div>
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[46%]">Account <span className="text-red-500">*</span></TableHead>
                  <TableHead className="text-right w-[22%]">Debit</TableHead>
                  <TableHead className="text-right w-[22%]">Credit</TableHead>
                  <TableHead className="w-[10%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {manualLines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Select value={l.account_id} onValueChange={(v) => setLine(i, { account_id: v })}>
                        <SelectTrigger data-testid={`cje-man-account-${i}`}><SelectValue placeholder="Pick account" /></SelectTrigger>
                        <SelectContent>{accounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" min="0" value={l.debit}
                             onChange={(e) => setLine(i, { debit: e.target.value, credit: e.target.value ? "" : l.credit })}
                             className="text-right tabular-nums h-8"
                             data-testid={`cje-man-debit-${i}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" min="0" value={l.credit}
                             onChange={(e) => setLine(i, { credit: e.target.value, debit: e.target.value ? "" : l.debit })}
                             className="text-right tabular-nums h-8"
                             data-testid={`cje-man-credit-${i}`} />
                    </TableCell>
                    <TableCell>
                      {manualLines.length > 2 && (
                        <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => removeLine(i)} data-testid={`cje-man-remove-${i}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-100 font-semibold">
                  <TableCell><Button size="sm" variant="outline" onClick={addLine} data-testid="cje-man-add-line"><Plus className="h-3 w-3 mr-1" /> Add line</Button></TableCell>
                  <TableCell className="text-right tabular-nums" data-testid="cje-man-total-dr">{fmtUSD(manualTotDr)}</TableCell>
                  <TableCell className="text-right tabular-nums" data-testid="cje-man-total-cr">{fmtUSD(manualTotCr)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
          {!manualBalanced && manualTotDr + manualTotCr > 0 && (
            <p className="text-xs text-red-600" data-testid="cje-man-imbalance">Dr and Cr totals must match and be greater than zero.</p>
          )}
        </div>
      )}

      {/* Payroll — reuses the same look as PayrollRunPanel but scoped to this modal. */}
      {jeType === "payroll" && (
        <div className="space-y-3" data-testid="cje-payroll-fields">
          <div className="grid grid-cols-4 gap-3">
            <div><Label className="text-xs">Year</Label><Input type="number" value={pyYear} onChange={(e) => setPyYear(e.target.value)} data-testid="cje-py-year" /></div>
            <div><Label className="text-xs">Month</Label><Input type="number" min="1" max="12" value={pyMonth} onChange={(e) => setPyMonth(e.target.value)} data-testid="cje-py-month" /></div>
            <div><Label className="text-xs">Payment date</Label><Input type="date" value={pyPayDate} onChange={(e) => setPyPayDate(e.target.value)} data-testid="cje-py-paydate" /></div>
            <div>
              <Label className="text-xs">Pay-from</Label>
              <Select value={pyBankCode} onValueChange={setPyBankCode}>
                <SelectTrigger data-testid="cje-py-bank"><SelectValue placeholder="Bank" /></SelectTrigger>
                <SelectContent>{banks.map((a) => (<SelectItem key={a.id} value={a.code}>{a.code} — {a.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} data-testid="cje-py-note" />
          </div>
          <div className="border rounded-md overflow-x-auto max-h-[320px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[22%]">Employee</TableHead>
                  <TableHead className="text-right w-[12%]">Snap Gross</TableHead>
                  <TableHead className="text-right w-[13%]">Actual Gross</TableHead>
                  <TableHead className="text-right w-[10%]">Snap PAYE</TableHead>
                  <TableHead className="text-right w-[13%]">Actual PAYE</TableHead>
                  <TableHead className="text-right w-[10%]">Other Ded.</TableHead>
                  <TableHead className="w-[20%]">Variance Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pyBusy && <TableRow><TableCell colSpan={7} className="text-center py-4"><Loader2 className="inline animate-spin h-4 w-4" /></TableCell></TableRow>}
                {(snapshot?.lines || []).map((l) => {
                  const ov = pyOverrides[l.employee_id] || {};
                  return (
                    <TableRow key={l.employee_id} className={l.has_variance ? "bg-amber-50" : ""}>
                      <TableCell className="font-medium">{l.employee_name}{l.has_variance && <Badge className="ml-1 bg-amber-200 text-amber-900">Variance</Badge>}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-500">{fmtUSD(l.snapshot.gross)}</TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" min="0"
                               value={ov.base_salary ?? l.actual.gross}
                               onChange={(e) => setPyOverrides((p) => ({ ...p, [l.employee_id]: { ...(p[l.employee_id] || {}), base_salary: e.target.value } }))}
                               onBlur={fetchSnapshot}
                               className="text-right tabular-nums h-8"
                               data-testid={`cje-py-actual-gross-${l.employee_id}`} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-500">{fmtUSD(l.snapshot.paye)}</TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" min="0"
                               value={ov.paye_tax ?? l.actual.paye}
                               onChange={(e) => setPyOverrides((p) => ({ ...p, [l.employee_id]: { ...(p[l.employee_id] || {}), paye_tax: e.target.value } }))}
                               onBlur={fetchSnapshot}
                               className="text-right tabular-nums h-8" />
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" min="0"
                               value={ov.other_deductions ?? l.actual.other_deductions}
                               onChange={(e) => setPyOverrides((p) => ({ ...p, [l.employee_id]: { ...(p[l.employee_id] || {}), other_deductions: e.target.value } }))}
                               onBlur={fetchSnapshot}
                               className="text-right tabular-nums h-8" />
                      </TableCell>
                      <TableCell>
                        {l.has_variance ? (
                          <Input value={ov.variance_note ?? ""}
                                 onChange={(e) => setPyOverrides((p) => ({ ...p, [l.employee_id]: { ...(p[l.employee_id] || {}), variance_note: e.target.value } }))}
                                 placeholder="Required"
                                 className="h-8"
                                 data-testid={`cje-py-note-${l.employee_id}`} />
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {snapshot?.totals && (
                  <TableRow className="bg-slate-100 font-semibold">
                    <TableCell>Totals</TableCell>
                    <TableCell className="text-right tabular-nums">—</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(snapshot.totals.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums">—</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(snapshot.totals.paye)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(snapshot.totals.other_deductions)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {proposedJe?.lines && (
            <div className="rounded-md border bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-600 mb-2">Proposed JE preview</p>
              <Table>
                <TableHeader><TableRow><TableHead>Account</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {proposedJe.lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{l.account_code} {l.account_name}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.debit > 0 ? fmtUSD(l.debit) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.credit > 0 ? fmtUSD(l.credit) : "—"}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-100 font-semibold">
                    <TableCell>Totals</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(proposedJe.total_debit)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(proposedJe.total_credit)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
          {varianceRowsMissingNote.length > 0 && (
            <p className="text-xs text-red-600">{varianceRowsMissingNote.length} variance row(s) require a note before submit.</p>
          )}
        </div>
      )}

      {/* Shared attachment picker for all types */}
      <AttachmentPicker attIds={attIds} setAttIds={setAttIds} />

      {/* Bug bundle #13 (2026-08-19) — live validation checklist. Shows
          exactly what's still missing so users never wonder why Submit is
          disabled. Auto-hides when the form is ready. */}
      {!canSubmit && missingReasons.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-[11px] text-slate-600"
             data-testid="cje-validation-summary">
          <div className="font-medium text-slate-700 mb-1">Before you can submit:</div>
          <ul className="space-y-0.5">
            {missingReasons.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5" data-testid={`cje-validation-item-${i}`}>
                <AlertTriangle className="h-3 w-3 mt-[2px] text-amber-500 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="cje-cancel">Cancel</Button>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            {/* Radix requires the trigger to be a single element; wrap the
                disabled Button in a span so hover still fires the tooltip. */}
            <TooltipTrigger asChild>
              <span className={!canSubmit ? "inline-block cursor-not-allowed" : "inline-block"}>
                <Button onClick={submit} disabled={!canSubmit}
                        className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                        data-testid="cje-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Submit for verification
                </Button>
              </span>
            </TooltipTrigger>
            {!canSubmit && missingReasons.length > 0 && (
              <TooltipContent side="top" className="max-w-xs">
                <div className="text-[11px] font-medium mb-1">Fill required fields first</div>
                <ul className="text-[10px] space-y-0.5">
                  {missingReasons.slice(0, 6).map((r, i) => (
                    <li key={i}>• {r}</li>
                  ))}
                  {missingReasons.length > 6 && (
                    <li className="opacity-70">…and {missingReasons.length - 6} more</li>
                  )}
                </ul>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </DialogFooter>
    </div>
  );
}

// Phase 36 (2026-07-17) — Source-of-Funds indicator pill.
const SOF_META = {
  loan_drawdown:     { label: "Loan drawdown",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  customer_payment:  { label: "Customer payment",  cls: "bg-sky-100 text-sky-800 border-sky-300" },
  physical_cash:     { label: "Physical cash",     cls: "bg-amber-100 text-amber-800 border-amber-300" },
  owner_capital:     { label: "Owner capital",     cls: "bg-purple-100 text-purple-800 border-purple-300" },
  opening_balance:   { label: "Opening balance",   cls: "bg-slate-100 text-slate-700 border-slate-300" },
  internal_transfer: { label: "Internal transfer", cls: "bg-teal-100 text-teal-800 border-teal-300" },
};
function SourceOfFundsPill({ sof }) {
  const meta = SOF_META[sof];
  if (!meta) return <span className="text-xs text-slate-400" data-testid="sof-pill-none">—</span>;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.cls}`}
      data-testid={`sof-pill-${sof}`}
    >
      {meta.label}
    </span>
  );
}

// Phase 52-F (2026-08-07) — Type filter chips for the unified Transactions
// list. Groups the underlying source_type / transaction_type so users can
// slice by "Payroll", "Loans", "Bank transfer", "Manual JE", or "All".
// Phase 5 (2026-08-09) — Unified navy palette. All Type filter chips
// (inactive) use the same shell as the inline row Type badges, so a
// row and its filter share one look. Active filter chip switches to a
// solid blue-600 fill so the current filter is unmistakable.
const _PILL_MUTED   = "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-300 dark:border-slate-500/60";
const _PILL_ACTIVE  = "bg-blue-600 text-white border border-blue-600";
const TYPE_CHIPS = [
  { key: "all",           label: "All",             cls: _PILL_MUTED },
  { key: "transactions",  label: "Transactions",    cls: _PILL_MUTED },
  { key: "manual_je",     label: "Manual JE",       cls: _PILL_MUTED },
  { key: "payroll",       label: "Payroll",         cls: _PILL_MUTED },
  { key: "loan",          label: "Loans",           cls: _PILL_MUTED },
  { key: "bank_transfer", label: "Bank transfer",   cls: _PILL_MUTED },
];

// Classify a JE row into a chip bucket for filtering.
function jeChipKey(je) {
  const s = je.source_type;
  if (s === "manual") return "manual_je";
  if (s === "payroll" || s === "payroll_void" || s === "payroll_direct") return "payroll";
  if (s === "loan" || s === "loan_accrual" || s === "loan_payoff") return "loan";
  if (s === "bank_transfer") return "bank_transfer";
  return "manual_je"; // fallback for uncategorised JEs
}

// Phase 4 (2026-08-09) — Classify a plain transaction row into the same
// chip bucket the top filter uses, so the inline "Type" badge on the
// row can be tinted with the EXACT SAME Tailwind classes as the filter
// chip. One colour per type, everywhere.
function txChipKey(tx) {
  const t = tx?.transaction_type;
  if (t === "payroll" || t === "payroll_disbursement") return "payroll";
  if (t === "bank_transfer") return "bank_transfer";
  return "transactions";
}

// Look up the chip class string by key. This is the ONE source of
// truth for per-type colouring — both the filter chip and the inline
// row badge read from the same TYPE_CHIPS table above.
function chipCls(key) {
  return (TYPE_CHIPS.find((c) => c.key === key) || TYPE_CHIPS[0]).cls;
}

// Pretty label for the "Type" column on a JE-sourced row.
function jeTypeLabel(je) {
  const s = je.source_type;
  const M = {
    manual: "Manual JE",
    payroll: "Payroll accrual",
    payroll_void: "Payroll void",
    payroll_direct: "Payroll (direct)",
    loan: "Loan",
    loan_accrual: "Loan accrual",
    loan_payoff: "Loan payoff",
    bank_transfer: "Bank transfer",
  };
  return M[s] || (s ? s.replace(/_/g, " ") : "JE");
}

// A JE-sourced row's "amount" for display: total_debit == total_credit
// on a balanced entry, so pick either.
function jeAmount(je) {
  return Number(je.total_debit || je.total_credit || 0);
}

// Phase 53 (2026-08-08) — stage badge for the new pipeline.
// Phase 5 (2026-08-09) — unified navy pill; only the label differs.
const STAGE_META = {
  draft:          { cls: _PILL_MUTED, label: "Draft" },
  pending_verify: { cls: _PILL_MUTED, label: "Pending Verify" },
  sent_back:      { cls: _PILL_MUTED, label: "Sent Back" },
  pending_post:   { cls: _PILL_MUTED, label: "Pending Post" },
  posted:         { cls: _PILL_MUTED, label: "Posted" },
  rejected:       { cls: _PILL_MUTED, label: "Rejected" },
};
// Map legacy status → new stage for rows that pre-date the pipeline.
const _STATUS_TO_STAGE = {
  draft: "draft", submitted: "pending_verify", under_review: "pending_verify",
  missing_document: "sent_back", clarification_needed: "sent_back",
  verified: "pending_post", processed: "pending_post",
  posted: "posted", reported: "posted",
  rejected: "rejected", deleted_by_admin: "rejected",
};
function deriveStage(row) {
  if (row?.raw?.stage && STAGE_META[row.raw.stage]) return row.raw.stage;
  return _STATUS_TO_STAGE[row.status] || "draft";
}
function StageBadge({ stage }) {
  const m = STAGE_META[stage] || STAGE_META.draft;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${m.cls}`}
          data-testid={`stage-badge-${stage}`}>
      {m.label}
    </span>
  );
}

export default function TransactionsPage() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  // Phase 19 PR-2 hot-fix (2026-07-06) — permission-key gate.
  const canCreate = can ? can("system.can_make_transaction") : false;
  const isAdmin = user?.role === ROLES.SYSTEM_ADMIN || user?.full_access === true;

  const [items, setItems] = useState([]);
  const [jeItems, setJeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Phase 52-F (2026-08-07) — Type filter chip selection for the unified view.
  const [typeChip, setTypeChip] = useState("all");
  // Phase 5 (2026-08-09) — "+ Create JE" modal replaces the Payroll-only
  // one. Six draft types, one endpoint, one pipeline.
  const [createJEOpen, setCreateJEOpen] = useState(false);
  // Bug bundle #4 (2026-08-10) — Row-click detail modal. Opens on the
  // Transactions page (no route change). Works for both tx-sourced and
  // JE-sourced rows. `detailRow` holds the merged row (tx OR je). When
  // set, an effect fetches the full JE payload if there is one (posted
  // tx → `journal_entry_id`; native JE → row.id). Read-only view.
  const [detailRow, setDetailRow] = useState(null);
  const [detailJE, setDetailJE] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);

  useEffect(() => {
    if (!detailRow) { setDetailJE(null); return; }
    const jeId = detailRow.__source === "je"
      ? detailRow.id
      : (detailRow.raw?.journal_entry_id || detailRow.raw?.je_id || null);
    if (!jeId) { setDetailJE(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setDetailBusy(true);
        const { data } = await api.get(`/journal-entries/${jeId}`);
        if (!cancelled) setDetailJE(data || null);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setDetailBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detailRow]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter !== "all") params.set("transaction_type", typeFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (isAdmin && includeDeleted) params.set("include_deleted", "true");
      // Fetch transactions + JEs in parallel, then merge. JEs whose
      // source_type is "transaction" are dropped since they duplicate
      // the transaction row 1-for-1.
      const jeParams = new URLSearchParams();
      if (dateFrom) jeParams.set("date_from", dateFrom);
      if (dateTo) jeParams.set("date_to", dateTo);
      jeParams.set("page_size", "200");
      const [txResp, jeResp] = await Promise.all([
        api.get(`/transactions?${params.toString()}`),
        api.get(`/journal-entries?${jeParams.toString()}`),
      ]);
      setItems(txResp.data.items || []);
      const jes = (jeResp.data.items || []).filter(
        (je) => je.source_type && je.source_type !== "transaction",
      );
      setJeItems(jes);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter, typeFilter, dateFrom, dateTo, includeDeleted]);

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusyId(confirmDel.id);
    try {
      const { data } = await api.delete(`/transactions/${confirmDel.id}`);
      toast.success(data.soft
        ? `Transaction soft-deleted (status set to deleted_by_admin, reversing JE posted).`
        : `Transaction permanently removed.`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  // Phase 52-F (2026-08-07) — merge txs and JEs into a single, chip-
  // filtered, date-sorted list. Each row carries `__source` so the
  // table can render tx rows normally and JE rows with the read-only
  // "Type" chip + paperclip.
  const merged = (() => {
    const rows = [];
    if (typeChip === "all" || typeChip === "transactions") {
      for (const t of items) {
        rows.push({
          __source: "tx",
          id: t.id,
          date: t.transaction_date,
          type: typeLabel(t.transaction_type),
          counterparty: t.counterparty_name || "—",
          // Phase 5+ (2026-08-09) — surface the user-entered description
          // (or narration if present) so the Narration column shows the
          // same text a user would recognise from Bookkeeping.
          narration: t.narration || t.description || t.note || "",
          amount: t.amount,
          status: t.status,
          sof: t.source_of_funds,
          submitted_at: t.submitted_at,
          raw: t,
        });
      }
    }
    for (const je of jeItems) {
      const chip = jeChipKey(je);
      if (typeChip !== "all" && typeChip !== chip) continue;
      rows.push({
        __source: "je",
        id: je.id,
        date: je.entry_date,
        type: jeTypeLabel(je),
        counterparty: je.reference || "—",
        narration: je.narration || "",
        amount: jeAmount(je),
        status: "posted",
        sof: null,
        submitted_at: je.posted_at,
        raw: je,
        chip,
      });
    }
    rows.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return rows;
  })();

  return (
    <div className="space-y-5" data-testid="transactions-page">
      <BackToDashBar />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Transactions</h2>
          <p className="text-sm text-slate-600 mt-1">Unified view — transactions and journal entries you are allowed to see.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIncludeDeleted((v) => !v)}
              data-testid="toggle-include-deleted"
              title="Toggle visibility of admin-deleted transactions"
            >
              {includeDeleted ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
              {includeDeleted ? "Hide deleted" : "Show deleted"}
            </Button>
          )}
          {canCreate && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  // Phase 29 (2026-07-17) — stream export.csv with the
                  // current filters as query params. Uses fetch to keep
                  // the auth header + trigger a file download.
                  const params = new URLSearchParams();
                  if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
                  if (typeFilter && typeFilter !== "all") params.set("transaction_type", typeFilter);
                  if (dateFrom) params.set("start_date", dateFrom);
                  if (dateTo) params.set("end_date", dateTo);
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/transactions/bulk/export.csv?${params.toString()}`;
                  const token = localStorage.getItem("st_token");
                  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                  if (!r.ok) throw new Error(`export failed: ${r.status}`);
                  const blob = await r.blob();
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  toast.success("Exported transactions to CSV");
                } catch (e) {
                  toast.error(String(e));
                }
              }}
              data-testid="export-csv-btn"
            >
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
          {/* Phase 6 (2026-08-09) — Unified entry point. The old
              "+ New transaction" and "Bulk add" buttons were retired.
              Every JE (Payroll, Income, Expense, Bank Transfer, Loan
              Repayment, Manual) now flows through "+ Create JE" so
              users have a single, consistent modal. */}
          {canCreate && (
            <Button
              className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
              size="sm"
              onClick={() => setCreateJEOpen(true)}
              data-testid="create-je-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Create Transaction
            </Button>
          )}
        </div>
      </div>

      {/* Phase 52-F (2026-08-07) — Type filter chip row. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="type-chip-row">
        <span className="text-xs text-slate-500 mr-1">Type:</span>
        {TYPE_CHIPS.map((c) => {
          const active = typeChip === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setTypeChip(c.key)}
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${active ? _PILL_ACTIVE : c.cls + " hover:brightness-95"}`}
              data-testid={`type-chip-${c.key}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Type</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {TX_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" />
          </div>
          <div>
            <label className="text-xs text-slate-500">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
            </div>
          ) : merged.length === 0 ? (
            <div className="text-center py-16">
              <FileSearch className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No entries match the current filters.</p>
              <p className="text-sm text-slate-400 mt-1">Try broadening the type chip or clearing filters.</p>
              {canCreate && (
                <Button
                  className="mt-4 bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                  onClick={() => setCreateJEOpen(true)}
                  data-testid="empty-create-je-btn"
                >
                  <Plus className="mr-2 h-4 w-4" /> Create Transaction
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <Table data-testid="tx-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Source / Reference</TableHead>
                      <TableHead>Counterparty</TableHead>
                      <TableHead className="max-w-[280px]">Narration</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead className="text-center">Files</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {merged.map((row) => (
                      row.__source === "tx" ? (
                        <TableRow
                          key={`tx-${row.id}`}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => setDetailRow(row)}
                          data-testid={`tx-row-${row.id}`}
                        >
                          <TableCell className="text-slate-700">{fmtDate(row.date)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${chipCls(txChipKey(row.raw))} text-[10px] font-medium`}>
                              {row.type}
                            </Badge>
                          </TableCell>
                          <TableCell><SourceOfFundsPill sof={row.sof} /></TableCell>
                          <TableCell className="text-slate-700">{row.counterparty}</TableCell>
                          <TableCell className="max-w-[280px]">
                            <p
                              className="text-slate-700 text-xs line-clamp-2 break-words"
                              title={row.narration || ""}
                              data-testid={`tx-narration-${row.id}`}
                            >
                              {row.narration || "—"}
                            </p>
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums">{fmtUSD(row.amount)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={STATUS_STYLE[row.status]}>
                              {STATUS_LABELS[row.status] || row.status}
                            </Badge>
                          </TableCell>
                          <TableCell><StageBadge stage={deriveStage(row)} /></TableCell>
                          <TableCell className="text-center text-slate-400 text-xs">—</TableCell>
                          <TableCell><OwnerCell created_by_email={row.raw.created_by_email} created_at={row.raw.created_at} updated_by_email={row.raw.posted_by_email || row.raw.reviewed_by_email} updated_at={row.raw.updated_at} /></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); setDetailRow(row); }}
                                data-testid={`tx-view-${row.id}`}
                              >
                                View
                              </Button>
                              <AdminTxActions tx={row.raw} onChanged={load} />
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow
                          key={`je-${row.id}`}
                          className="cursor-pointer hover:bg-slate-50 bg-slate-50/40"
                          onClick={() => setDetailRow(row)}
                          data-testid={`je-row-${row.id}`}
                        >
                          <TableCell className="text-slate-700">{fmtDate(row.date)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${chipCls(row.chip)} text-[10px] font-medium`}>
                              <Lock className="h-3 w-3 mr-1" /> {row.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-slate-600">{row.counterparty}</TableCell>
                          <TableCell className="text-slate-600 text-xs italic">JE · read-only</TableCell>
                          <TableCell className="max-w-[280px]">
                            <p
                              className="text-slate-700 text-xs line-clamp-2 break-words"
                              title={row.narration || ""}
                              data-testid={`je-narration-${row.id}`}
                            >
                              {row.narration || "—"}
                            </p>
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums text-slate-700">{fmtUSD(row.amount)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">
                              posted
                            </Badge>
                          </TableCell>
                          <TableCell><StageBadge stage="posted" /></TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`inline-flex items-center gap-1 text-xs ${(row.raw.attachments?.length || 0) > 0 ? "text-sky-700 font-medium" : "text-slate-400"}`}
                              data-testid={`je-tx-attach-${row.id}`}
                              title={`${row.raw.attachments?.length || 0} attachment(s)`}
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                              {row.raw.attachments?.length || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{row.raw.posted_by_email || "—"}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); setDetailRow(row); }}
                              data-testid={`je-open-${row.id}`}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Mobile card list */}
              <div className="md:hidden divide-y divide-slate-200">
                {merged.map((row) => (
                  row.__source === "tx" ? (
                    <button
                      type="button"
                      key={`tx-${row.id}`}
                      onClick={() => setDetailRow(row)}
                      className="w-full text-left block px-4 py-3"
                      data-testid={`tx-card-${row.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {row.counterparty || row.type}
                          </p>
                          <p className="text-xs text-slate-500">{fmtDate(row.date)} · {row.type}</p>
                          {row.narration && (
                            <p className="text-xs text-slate-600 mt-0.5 line-clamp-2 break-words"
                               title={row.narration}
                               data-testid={`tx-card-narration-${row.id}`}>
                              {row.narration}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold tabular-nums">{fmtUSD(row.amount)}</p>
                          <Badge variant="outline" className={`${STATUS_STYLE[row.status]} text-[10px]`}>
                            {STATUS_LABELS[row.status] || row.status}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      key={`je-${row.id}`}
                      onClick={() => setDetailRow(row)}
                      className="w-full text-left block px-4 py-3 bg-slate-50/40"
                      data-testid={`je-card-${row.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {row.counterparty}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Lock className="h-3 w-3" /> {fmtDate(row.date)} · {row.type}
                          </p>
                          {row.narration && (
                            <p className="text-xs text-slate-600 mt-0.5 line-clamp-2 break-words"
                               title={row.narration}
                               data-testid={`je-card-narration-${row.id}`}>
                              {row.narration}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold tabular-nums text-slate-700">{fmtUSD(row.amount)}</p>
                          {(row.raw.attachments?.length || 0) > 0 && (
                            <span className="text-[10px] text-sky-700"><Paperclip className="inline h-3 w-3 mr-0.5" />{row.raw.attachments.length}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Phase 9c Fix #8 — admin delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent data-testid="admin-delete-tx-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Delete transaction (admin override)</DialogTitle>
            <DialogDescription>
              You are about to delete <span className="font-mono text-slate-700">{confirmDel?.reference_number || confirmDel?.id?.slice(0, 8)}</span>{" "}
              ({STATUS_LABELS[confirmDel?.status] || confirmDel?.status}).
              {confirmDel && ["processed", "posted", "reported"].includes(confirmDel.status) ? (
                <span className="block mt-2 text-rose-700">
                  This transaction has been posted to the General Ledger. Deleting it will
                  create a <strong>reversing journal entry</strong> to keep the books balanced.
                  The original record will be marked <code>deleted_by_admin</code> and preserved
                  for audit. Continue?
                </span>
              ) : (
                <span className="block mt-2 text-rose-700">
                  This record is pre-posting; it will be <strong>permanently removed</strong> from
                  the database. Continue?
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete}
                    disabled={busyId === confirmDel?.id}
                    data-testid="confirm-admin-delete-tx">
              {busyId === confirmDel?.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 5 (2026-08-09) — Unified "+ Create Transaction" modal. Six
          draft types (payroll, income, expense, bank_transfer,
          loan_repayment, manual), all flow through the Phase 53 pipeline.
          Attachments picked up in the modal are transferred to the JE at
          post-time. Phase 6 (2026-08-09) — user-facing wording switched
          from "Journal Entry" to "Transaction" — same mechanics, more
          approachable label for non-accountants. */}
      <Dialog open={createJEOpen} onOpenChange={setCreateJEOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="create-je-modal">
          <DialogHeader>
            <DialogTitle>Create Transaction</DialogTitle>
            <DialogDescription>
              Pick a type, fill the required fields, optionally attach
              supporting documents, and submit. The transaction lands in
              the admin verification queue; admins can verify and post
              their own drafts for speed.
            </DialogDescription>
          </DialogHeader>
          <CreateJEPanel
            onCancel={() => setCreateJEOpen(false)}
            onCreated={() => {
              setCreateJEOpen(false);
              toast.success("Draft submitted for verification.");
              load();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Bug bundle #4 (2026-08-10) — Row-click JE detail modal. Opens
          for BOTH tx-sourced rows (fetches the linked JE by
          `journal_entry_id`) and JE-sourced rows (fetches by row.id).
          Read-only. Closes on Escape / backdrop click / X. Does NOT
          navigate away from /transactions. */}
      <TxDetailModal
        row={detailRow}
        je={detailJE}
        busy={detailBusy}
        onClose={() => setDetailRow(null)}
      />
    </div>
  );
}

// ---- Bug bundle #4 (2026-08-10) ---------------------------------------
// JE detail modal. Renders the header (date/type/counterparty/reference/
// stage/status), narration, Dr/Cr lines, totals + Balanced pill, attachments,
// stage-history timeline, and metadata. Read-only.
//
// Bug bundle #2/#2 (2026-08-10) — Also usable from other pages
// (Expenses Breakdown drill-down) via `<TxDetailModal jeId={id} onClose>`.
// In that mode we skip the tx-only meta (row.__source, tx.raw) and just
// fetch the JE + render lines / attachments / metadata.
export function TxDetailModal({ row, je, busy, onClose, jeId }) {
  // Standalone mode: `jeId` passed without a `row` — fetch the JE
  // ourselves and render with a synthetic row derived from the JE.
  const [standaloneJe, setStandaloneJe] = useState(null);
  const [standaloneBusy, setStandaloneBusy] = useState(false);
  // Bug bundle #9 (2026-08-11) — sub-modal state must live above any
  // early return; otherwise React hooks rules complain.
  const [editMetaOpen, setEditMetaOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  // Bug bundle #11 (2026-08-18) — correction bridge inside TxDetailModal
  // so users who reach a JE from the Transactions page (or a drill-down)
  // get the same auto-open Create JE flow as the Bookkeeping page.
  const [correctionDraft, setCorrectionDraft] = useState(null);
  const { user: currentUser } = useAuth();
  const standalone = !!jeId && !row;

  useEffect(() => {
    if (!standalone) return;
    let cancelled = false;
    (async () => {
      try {
        setStandaloneBusy(true);
        const { data } = await api.get(`/journal-entries/${jeId}`);
        if (!cancelled) setStandaloneJe(data || null);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setStandaloneBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jeId, standalone]);

  if (!row && !jeId) return null;

  const effectiveRow = standalone && standaloneJe ? {
    __source: "je",
    id: standaloneJe.id,
    date: standaloneJe.entry_date,
    type: standaloneJe.source_type === "loan" ? "Loan"
        : standaloneJe.source_type === "payroll" ? "Payroll"
        : (standaloneJe.source_type || "JE"),
    counterparty: standaloneJe.counterparty_name || standaloneJe.reference || "—",
    narration: standaloneJe.narration || "",
    amount: (standaloneJe.lines || []).reduce(
      (s, l) => s + Number(l.debit || 0), 0,
    ),
    status: "posted",
    raw: standaloneJe,
    chip: null,
  } : row;

  if (!effectiveRow) {
    // Standalone mode is still fetching — render an empty placeholder
    // dialog with a spinner so Escape / backdrop still close it cleanly.
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl" data-testid="tx-detail-modal-loading">
          <DialogHeader>
            <DialogTitle>Loading journal entry…</DialogTitle>
            <DialogDescription className="sr-only">Loading</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Fetching JE…
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isTx = effectiveRow.__source === "tx";
  const tx = isTx ? (effectiveRow.raw || {}) : null;
  const jeDoc = je || (standalone ? standaloneJe : (isTx ? null : (effectiveRow.raw || null)));
  const effectiveBusy = busy || standaloneBusy;
  const lines = (jeDoc?.lines) || [];
  const totalDr = lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCr = lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.005;
  const stage = deriveStage(effectiveRow);
  const stageHist = tx?.stage_history || [];
  const attachments = jeDoc?.attachments || tx?.attachments || [];
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("st_token") : null;

  // Bug bundle #9 (2026-08-11) — JE editability state.
  const isAdmin = currentUser?.role === "system_admin";
  const hasFullAccess = currentUser?.full_access === true;
  const canReverse = jeDoc?.id && (isAdmin || hasFullAccess);
  // Metadata edits allowed for admin, full_access, OR original poster w/in 24h
  const postedAt = jeDoc?.posted_at ? new Date(jeDoc.posted_at) : null;
  const isRecentPoster = jeDoc && currentUser
    && jeDoc.posted_by === currentUser.id
    && postedAt && (Date.now() - postedAt.getTime()) < 86_400_000;
  const canEditMeta = jeDoc?.id && (isAdmin || hasFullAccess || isRecentPoster);
  const isSuperseded = !!jeDoc?.superseded_by_reversal_je_id;
  const isReversal = jeDoc?.source_type === "reversal";
  const isCorrection = !!jeDoc?.corrects_je_id;
  const wasEdited = (jeDoc?.metadata_edit_count || 0) > 0;

  const downloadAttachment = async (att) => {
    try {
      const url = `${backendUrl}/api/attachments/${att.id || att.attachment_id}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Download failed: ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = att.filename || att.name || `attachment-${att.id || ""}`;
      a.click();
    } catch (e) { toast.error(String(e)); }
  };

  return (
    <Dialog open={!!(effectiveRow)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="tx-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>Transaction detail</span>
            <Badge variant="outline" className={`${chipCls(isTx ? txChipKey(tx) : (effectiveRow.chip || "manual"))} text-[10px]`}>
              {effectiveRow.type}
            </Badge>
            <StageBadge stage={stage} />
            {effectiveRow.status && (
              <Badge variant="outline" className={STATUS_STYLE[effectiveRow.status] || "bg-slate-100 text-slate-700"}>
                {STATUS_LABELS[effectiveRow.status] || effectiveRow.status}
              </Badge>
            )}
            {/* Bug bundle #9 (2026-08-11) — reversal / superseded / correction / edited badges */}
            {isSuperseded && (
              <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200 text-[10px]" data-testid="badge-superseded">
                Superseded
              </Badge>
            )}
            {isReversal && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-300 text-[10px]" data-testid="badge-reversal">
                Reversal
              </Badge>
            )}
            {isCorrection && (
              <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 border border-sky-300 text-[10px]"
                     title={`Corrects ${jeDoc?.corrects_je_id?.slice(0,8)}…`}
                     data-testid="badge-correction">
                Correction
              </Badge>
            )}
            {wasEdited && (
              <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 border border-amber-200 text-[10px]"
                     title={`Last edited by ${jeDoc?.metadata_last_edited_by_email || "?"} · ${jeDoc?.metadata_edit_count} change(s)`}
                     data-testid="badge-edited">
                Edited
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Read-only JE view</DialogDescription>
        </DialogHeader>

        {/* Superseded watermark banner */}
        {isSuperseded && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="superseded-banner">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              <strong>Superseded</strong> — this JE was reversed on
              {" " + (jeDoc.superseded_at || "").slice(0, 10)}
              {" by " + (jeDoc.superseded_by_email || "?")}.
              {jeDoc.corrected_by_je_id && (
                <>
                  {" A correcting JE was posted. "}
                  <button className="underline text-amber-900" onClick={() => onClose && onClose()}>
                    Close and open Correcting JE
                  </button>
                </>
              )}
            </span>
          </div>
        )}

        {/* Admin action row — only shown when we have a JE document */}
        {jeDoc?.id && (canEditMeta || canReverse) && !isReversal && (
          <div className="mt-2 flex items-center gap-2" data-testid="je-admin-actions">
            {canEditMeta && !isSuperseded && (
              <Button
                variant="outline" size="sm"
                onClick={() => setEditMetaOpen(true)}
                data-testid="edit-metadata-btn"
                className="border-slate-300 text-slate-700"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit metadata
              </Button>
            )}
            {canReverse && !isSuperseded && !isCorrection && (
              <Button
                variant="outline" size="sm"
                onClick={() => setReverseOpen(true)}
                data-testid="correct-entry-btn"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Correct entry
              </Button>
            )}
          </div>
        )}

        {/* Header meta */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2 text-xs" data-testid="detail-header-meta">
          <div>
            <div className="text-slate-500">Date</div>
            <div className="font-semibold text-slate-800">{fmtDate(effectiveRow.date)}</div>
          </div>
          <div>
            <div className="text-slate-500">Counterparty</div>
            <div className="font-semibold text-slate-800 truncate" title={effectiveRow.counterparty}>{effectiveRow.counterparty || "—"}</div>
          </div>
          <div>
            <div className="text-slate-500">Reference #</div>
            <div className="font-mono text-slate-800 truncate">
              {tx?.reference_number || jeDoc?.reference || "—"}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Amount</div>
            <div className="font-semibold text-slate-900 tabular-nums">{fmtUSD(effectiveRow.amount)}</div>
          </div>
        </div>

        {/* Narration (full, untruncated) */}
        <div className="mt-4">
          <div className="text-xs text-slate-500 mb-1">Narration</div>
          <p className="text-sm text-slate-800 whitespace-pre-wrap break-words" data-testid="detail-narration">
            {effectiveRow.narration || "—"}
          </p>
        </div>

        {/* Dr/Cr lines */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Journal Entry Lines</div>
            {balanced && lines.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-medium"
                    data-testid="detail-balanced-pill">
                Balanced ✓
              </span>
            )}
          </div>
          {effectiveBusy ? (
            <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="detail-busy">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading JE…
            </div>
          ) : lines.length === 0 ? (
            <div className="text-sm text-slate-500 italic" data-testid="detail-no-lines">
              {isTx && !tx?.journal_entry_id
                ? "This draft has not been posted yet — the JE will appear here after Verify + Post."
                : "No lines on this journal entry."}
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="w-full text-sm" data-testid="detail-lines-table">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Account</th>
                    <th className="text-right px-3 py-2">Debit</th>
                    <th className="text-right px-3 py-2">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={`ln-${i}`} className="border-t border-slate-100" data-testid={`detail-line-${i}`}>
                      <td className="px-3 py-2 font-mono text-slate-700">{l.account_code}</td>
                      <td className="px-3 py-2 text-slate-800">{l.account_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{Number(l.debit || 0) > 0 ? fmtUSD(l.debit) : ""}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{Number(l.credit || 0) > 0 ? fmtUSD(l.credit) : ""}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    <td className="px-3 py-2" colSpan={2}>Totals</td>
                    <td className="px-3 py-2 text-right tabular-nums" data-testid="detail-total-debit">{fmtUSD(totalDr)}</td>
                    <td className="px-3 py-2 text-right tabular-nums" data-testid="detail-total-credit">{fmtUSD(totalCr)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Attachments</div>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded" data-testid="detail-attachments">
              {attachments.map((att, i) => (
                <li key={`att-${i}`} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`detail-attach-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="truncate text-slate-700" title={att.filename || att.name}>
                      {att.filename || att.name || "attachment"}
                    </span>
                    {att.size_bytes && (
                      <span className="text-xs text-slate-400">
                        {(att.size_bytes / 1024).toFixed(1)} KB
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadAttachment(att)}
                    data-testid={`detail-attach-download-${i}`}
                  >
                    <Download className="h-3 w-3 mr-1" /> Download
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Stage history */}
        {stageHist.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Stage history</div>
            <ol className="border-l-2 border-slate-200 pl-4 space-y-2" data-testid="detail-stage-history">
              {stageHist.map((s, i) => (
                <li key={`sh-${i}`} className="text-xs" data-testid={`detail-stage-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StageBadge stage={s.to_stage} />
                    <span className="text-slate-500">
                      by {s.by_email || s.actor_email || "system"}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">
                      {fmtDate((s.at || s.timestamp || "").slice(0, 10))}
                      {" "}{(s.at || s.timestamp || "").slice(11, 19)}
                    </span>
                  </div>
                  {s.comment && (
                    <p className="mt-1 text-slate-600 whitespace-pre-wrap break-words">
                      &ldquo;{s.comment}&rdquo;
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Metadata */}
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-500 border-t border-slate-100 pt-3" data-testid="detail-metadata">
          {(tx?.created_by_email || jeDoc?.posted_by_email) && (
            <div>
              <div>Created by</div>
              <div className="text-slate-700">{tx?.created_by_email || jeDoc?.posted_by_email}</div>
            </div>
          )}
          {(tx?.created_at || jeDoc?.posted_at) && (
            <div>
              <div>Created at</div>
              <div className="text-slate-700">{(tx?.created_at || jeDoc?.posted_at || "").slice(0, 19).replace("T", " ")}</div>
            </div>
          )}
          {jeDoc?.posted_by_email && (
            <div>
              <div>Posted by</div>
              <div className="text-slate-700">{jeDoc.posted_by_email}</div>
            </div>
          )}
          {jeDoc?.posted_at && (
            <div>
              <div>Posted at</div>
              <div className="text-slate-700">{jeDoc.posted_at.slice(0, 19).replace("T", " ")}</div>
            </div>
          )}
          {jeDoc?.id && (
            <div className="col-span-2">
              <div>JE ID</div>
              <div className="font-mono text-slate-700 text-[11px]">{jeDoc.id}</div>
            </div>
          )}
        </div>

        {/* Bug bundle #9 (2026-08-11) — Correction Trail timeline */}
        {jeDoc && (isSuperseded || isReversal || isCorrection) && (
          <div className="mt-5 border-t border-slate-100 pt-3" data-testid="correction-trail">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Correction Trail
            </div>
            <ol className="border-l-2 border-amber-200 pl-4 space-y-2 text-xs">
              {isReversal && jeDoc.original_je_id && (
                <li>
                  <span className="text-amber-700 font-medium">Reversal of</span>{" "}
                  <span className="font-mono text-slate-700">{jeDoc.original_je_id.slice(0, 8)}…</span>{" "}
                  posted <span className="text-slate-500">{(jeDoc.posted_at || "").slice(0, 10)}</span>{" "}
                  by <span className="text-slate-700">{jeDoc.posted_by_email}</span>
                </li>
              )}
              {isSuperseded && (
                <>
                  <li>
                    <span className="text-slate-800 font-medium">Reversed on</span>{" "}
                    <span className="text-slate-500">{(jeDoc.superseded_at || "").slice(0, 10)}</span>{" "}
                    by <span className="text-slate-700">{jeDoc.superseded_by_email || "?"}</span>
                    {jeDoc.supersede_reason && (
                      <p className="mt-0.5 italic text-slate-600 whitespace-pre-wrap">
                        &ldquo;{jeDoc.supersede_reason}&rdquo;
                      </p>
                    )}
                  </li>
                  <li>
                    <span className="text-slate-800 font-medium">Reversal JE</span>{" "}
                    <span className="font-mono text-slate-700">{(jeDoc.superseded_by_reversal_je_id || "").slice(0, 8)}…</span>
                  </li>
                  {jeDoc.corrected_by_je_id && (
                    <li>
                      <span className="text-sky-700 font-medium">Corrected by JE</span>{" "}
                      <span className="font-mono text-slate-700">{jeDoc.corrected_by_je_id.slice(0, 8)}…</span>
                    </li>
                  )}
                </>
              )}
              {isCorrection && jeDoc.corrects_je_id && (
                <li>
                  <span className="text-sky-700 font-medium">Corrects</span>{" "}
                  <span className="font-mono text-slate-700">{jeDoc.corrects_je_id.slice(0, 8)}…</span>
                </li>
              )}
            </ol>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="detail-close-btn">Close</Button>
        </div>
      </DialogContent>

      {/* Bug bundle #9 (2026-08-11) — Edit metadata sub-modal */}
      <EditMetadataModal
        open={editMetaOpen}
        onClose={() => setEditMetaOpen(false)}
        je={jeDoc}
        onSaved={() => { setEditMetaOpen(false); onClose && onClose(); }}
      />
      {/* Bug bundle #9 (2026-08-11) — Reverse & correct sub-modal */}
      <ReverseAndCorrectModal
        open={reverseOpen}
        onClose={() => setReverseOpen(false)}
        je={jeDoc}
        onDone={(draft) => {
          setReverseOpen(false);
          // Bug bundle #11 (2026-08-18) — auto-open Create JE prefilled
          // with correction draft, THEN close the detail modal so the
          // user lands directly in the correction editor.
          if (draft) setCorrectionDraft(draft);
          else onClose && onClose();
        }}
      />
      {/* Bug bundle #11 (2026-08-18) — Create Correcting JE dialog */}
      <Dialog open={!!correctionDraft} onOpenChange={(o) => {
        if (!o) { setCorrectionDraft(null); onClose && onClose(); }
      }}>
        <DialogContent className="max-w-3xl" data-testid="correction-create-dialog-tx">
          <DialogHeader>
            <DialogTitle>Create Correcting JE</DialogTitle>
            <DialogDescription>
              Adjust the amounts / accounts below, then submit. The correcting
              entry will link back to the original once posted.
            </DialogDescription>
          </DialogHeader>
          {correctionDraft && (
            <CreateJEPanel
              correctionDraft={correctionDraft}
              onCancel={() => { setCorrectionDraft(null); onClose && onClose(); }}
              onCreated={() => {
                setCorrectionDraft(null);
                onClose && onClose();
                toast.success(
                  "Correcting draft submitted — verify + post it to complete the chain."
                );
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Bug bundle #9 (2026-08-11) — Edit metadata + Reverse & Correct sub-modals
// ─────────────────────────────────────────────────────────────────────────

export function EditMetadataModal({ open, onClose, je, onSaved }) {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (je) {
      setForm({
        narration: je.narration || "",
        reference: je.reference || "",
        entry_date: je.entry_date || "",
        counterparty_name: je.counterparty_name || "",
      });
      setReason("");
    }
  }, [je?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!je) return null;

  const dateChanged = form.entry_date !== (je.entry_date || "");
  const dirty = ["narration", "reference", "entry_date", "counterparty_name"]
    .some((k) => (form[k] || "") !== (je[k] || ""));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of ["narration", "reference", "entry_date", "counterparty_name"]) {
        if ((form[k] || "") !== (je[k] || "")) payload[k] = form[k] || "";
      }
      if (dateChanged) payload.reason = reason.trim();
      await api.patch(`/journal-entries/${je.id}/metadata`, payload);
      toast.success("JE metadata updated");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="edit-metadata-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-slate-600" /> Edit JE metadata
          </DialogTitle>
          <DialogDescription>
            Safe fields only. Amounts, accounts and line items are locked — use &ldquo;Correct entry&rdquo; for financial edits.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Narration</Label>
            <Textarea value={form.narration || ""} rows={2}
                      onChange={(e) => setForm({ ...form, narration: e.target.value })}
                      data-testid="edit-narration"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Reference</Label>
              <Input value={form.reference || ""}
                     onChange={(e) => setForm({ ...form, reference: e.target.value })}
                     data-testid="edit-reference"/>
            </div>
            <div>
              <Label className="text-xs">Entry date</Label>
              <Input type="date" value={form.entry_date || ""}
                     onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                     data-testid="edit-entry-date"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Counterparty name</Label>
            <Input value={form.counterparty_name || ""}
                   onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })}
                   data-testid="edit-counterparty"/>
          </div>
          {dateChanged && (
            <div>
              <Label className="text-xs">
                Reason for changing entry date <span className="text-slate-400">(min 10 chars)</span>
              </Label>
              <Textarea value={reason} rows={2}
                        placeholder="Why is the entry date being changed?"
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="edit-date-reason"/>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!dirty || saving || (dateChanged && reason.trim().length < 10)}
            data-testid="edit-metadata-save"
            className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
          >
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReverseAndCorrectModal({ open, onClose, je, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Bug bundle #10 (2026-08-18) — in-modal error banner. Keeps the modal
  // open on failure and surfaces the server's error message alongside the
  // usual toast so the user can retry without re-typing the reason.
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setReason(""); setError(null); } }, [open]);

  const submit = async () => {
    if (!je) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/journal-entries/${je.id}/reverse-and-correct`, {
        reason: reason.trim(),
      });
      toast.success(
        `Reversal posted: ${data.reversal_reference}. Opening the Create Transaction modal for the correcting entry.`,
        { duration: 6000 }
      );
      // Bug bundle #11 (2026-08-18) — pass the correction_draft up to
      // the parent (Bookkeeping / Transactions page) so it can open the
      // Create Transaction modal in correction mode. `corrects_je_id`
      // is added so the eventual JE post can auto-link the pair.
      onDone && onDone({
        ...data.correction_draft,
        corrects_je_id: je.id,
        original_reference: je.reference || null,
      });
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    }
    finally { setBusy(false); }
  };

  if (!je) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="reverse-correct-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-800">
            <RotateCcw className="h-5 w-5" /> Reverse &amp; Correct Journal Entry
          </DialogTitle>
          <DialogDescription>
            Post a reversal, then create a correcting JE. The original is preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          This will (1) post a reversal JE (mirror of the original, dated today),
          (2) prepare a correction draft that you&apos;ll submit through the normal
          pipeline, (3) mark the original JE as <strong>Superseded</strong>{" "}
          (still visible in the ledger). The original JE will NOT be deleted.
        </div>
        {/* Bug bundle #10 (2026-08-18) — in-modal error banner */}
        {error && (
          <div className="mt-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900" data-testid="reverse-error-banner">
            <strong>Reversal failed:</strong> {error}
          </div>
        )}
        <div className="mt-3 space-y-1">
          <Label className="text-xs">Reason <span className="text-slate-400">(min 15 chars, required)</span></Label>
          <Textarea rows={3} value={reason}
                    placeholder="e.g. 'Original supplier picked was wrong; needs to hit different account'"
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="reverse-reason"/>
          <p className="text-xs text-slate-500">{reason.length}/500 chars</p>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700 text-white"
            disabled={busy || reason.trim().length < 15}
            onClick={submit}
            data-testid="reverse-submit-btn"
          >
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Reverse &amp; Correct
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
